import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import { getListV3, getListFilters, getListV3WithFilter } from "../../services/restAPI/ListsV2";
import { Pagination } from "../BP2Components";

import "./ListTable.less";
import IntlMessages from "../../util/IntlMessages";

interface SimpleListTableProps {
   listId: string;
   defaultFilterId?: string;
}

const DISPLAY_PRIORITY = ["abbreviation", "name", "label", "title", "answer", "content", "value"];
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}(T[\d:.]+(\.\d+)?(Z|[+-]\d{2}:\d{2})?)?$/;
const OL_STYLE = { margin: 0, paddingLeft: "1.2rem" };

const firstPrimitive = (obj: Record<string, any>): string => {
   for (const val of Object.values(obj)) {
      if (val != null && typeof val !== "object") return String(val);
   }
   return "";
};

const extractDisplayValue = (obj: Record<string, any>): string => {
   for (const key of DISPLAY_PRIORITY) {
      if (obj[key] != null && typeof obj[key] !== "object") return String(obj[key]);
   }
   for (const val of Object.values(obj)) {
      if (val && typeof val === "object" && !Array.isArray(val)) {
         for (const key of DISPLAY_PRIORITY) {
            if ((val as any)[key] != null && typeof (val as any)[key] !== "object")
               return String((val as any)[key]);
         }
         const fallback = firstPrimitive(val as Record<string, any>);
         if (fallback) return fallback;
      }
   }
   return firstPrimitive(obj);
};

const renderValue = (value: any): React.ReactNode => {
   if (value === null || value === undefined) return "";
   if (typeof value === "string" && ISO_DATE_RE.test(value)) {
      const m = moment(value);
      return m.isValid() ? m.format("DD.MM.YYYY HH:mm") : value;
   }
   if (Array.isArray(value)) {
      if (value.length === 0) return "";
      return (
         <ol style={OL_STYLE}>
            {value.map((item, i) => (
               <li key={i}>
                  {item && typeof item === "object"
                     ? extractDisplayValue(item)
                     : String(item ?? "")}
               </li>
            ))}
         </ol>
      );
   }
   if (typeof value === "object") {
      return extractDisplayValue(value);
   }
   return String(value);
};

type FilterResolution = { forListId: string; filterId: string | null } | null;

const SimpleListTable: React.FC<SimpleListTableProps> = ({ listId, defaultFilterId }) => {
   const [rows, setRows] = useState<any[]>([]);
   const [loading, setLoading] = useState(false);
   const [hasFetched, setHasFetched] = useState(false);
   const [page, setPage] = useState(1);
   const [listSize, setListSize] = useState<number | null>(null);
   const wrapperRef = useRef<HTMLDivElement>(null);
   const prevListIdRef = useRef<string>(listId);
   const [filterResolution, setFilterResolution] = useState<FilterResolution>(null);

   const onPaginationChange = useCallback((e) => {
      if (e === 0) return;
      (document.activeElement as HTMLElement)?.blur();
      setPage(e);
   }, []);

   useEffect(() => {
      if (!listId) return;
      setRows([]);
      if (defaultFilterId) {
         setFilterResolution({ forListId: listId, filterId: defaultFilterId });
         return;
      }
      let cancelled = false;
      getListFilters(listId)
         .then((res) => {
            if (cancelled) return;
            setFilterResolution({
               forListId: listId,
               filterId: res?.data?.find((f: any) => f.active)?.id ?? null,
            });
         })
         .catch(() => {
            if (!cancelled) setFilterResolution({ forListId: listId, filterId: null });
         });
      return () => {
         cancelled = true;
      };
   }, [listId, defaultFilterId]);

   useEffect(() => {
      if (!listId || !filterResolution || filterResolution.forListId !== listId) return;

      if (prevListIdRef.current !== listId) {
         prevListIdRef.current = listId;
         if (page !== 1) {
            setPage(1);
            return; // page change will re-trigger this effect with page=1
         }
      }

      let cancelled = false;
      const fetchList = async () => {
         setLoading(true);
         try {
            const res = filterResolution.filterId
               ? await getListV3WithFilter(listId, page, 10, filterResolution.filterId)
               : await getListV3(listId, page, 10);
            if (cancelled) return;
            setRows(res?.data?.data ?? []);
            const total = Number(res?.data?.meta?.totalElements);
            setListSize(Number.isFinite(total) ? total : null);
            wrapperRef.current?.scrollTo({ top: 0 });
         } catch (err) {
            if (!cancelled) {
               console.error("Failed to fetch list", err);
               setRows([]);
            }
         } finally {
            if (!cancelled) {
               setLoading(false);
               setHasFetched(true);
            }
         }
      };

      fetchList();
      return () => {
         cancelled = true;
      };
   }, [listId, page, filterResolution]);

   const columns = useMemo(() => {
      const seen = new Set<string>();
      for (const row of rows) {
         for (const key of Object.keys(row)) {
            seen.add(key);
         }
      }
      return Array.from(seen);
   }, [rows]);

   if (loading) return <div>Loading list…</div>;
   if (!listId)
      return (
         <div>
            <IntlMessages id="form.add-list-id" defaultMessage="Skonfiguruj identyfikator listy" />
         </div>
      );
   if (!filterResolution || filterResolution.forListId !== listId) {
      return null;
   }
   if (!rows.length && hasFetched) return <div>Brak danych</div>;

   return (
      <>
         <div className="lb-table-wrapper" ref={wrapperRef}>
            <table className="lb-table" style={{ width: "max-content", minWidth: "100%" }}>
               <thead>
                  <tr>
                     {columns.map((col) => (
                        <th key={col} className="slt-header">
                           {col}
                        </th>
                     ))}
                  </tr>
               </thead>
               <tbody>
                  {rows.map((row, idx) => (
                     <tr key={idx}>
                        {columns.map((col) => {
                           const value = row[col];
                           return (
                              <td
                                 key={col}
                                 className={`slt-cell${Array.isArray(value) ? " slt-cell--wide" : ""}`}
                              >
                                 <div className="slt-cell-content">{renderValue(value)}</div>
                              </td>
                           );
                        })}
                     </tr>
                  ))}
               </tbody>
            </table>
         </div>

         {listSize != null && listSize > 0 && (
            <Pagination
               initialPage={page}
               defaultPageSize={10}
               onChange={onPaginationChange}
               totalItems={listSize}
            />
         )}
      </>
   );
};

export default SimpleListTable;
